// 21+ age-verification helpers — byte-faithful ports of the Android register (InStorePaymentFragment
// showAgeVerificationDialog* / AgeVerificationViewModel / UtilsMethod). Pure functions: every rule
// here is unit-tested against the Kotlin behaviour, including its quirks (they are what the POS does).

const pad2 = (n) => String(n).padStart(2, "0");
const daysInMonth = (y, m1) => new Date(y, m1, 0).getDate(); // m1 = 1..12
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const ID_CARD_TYPE = "Driver's License or ID Card"; // R.string.drivers_license_identification_card
export const VERIFY_NON_ID_PERSON = "verify_non_id_person"; // UtilsConstant.VERIFY_NON_ID_PERSON

// "Aug 27th, 2005" — today's month/day + (year − 21). Android derives the suffix from `day % 10`
// only, so the 11th/12th/13th read "11st/12nd/13rd"; kept as-is for parity.
export function cutoffSource(now = new Date()) {
  const d = now.getDate();
  const r = d % 10;
  const suffix = r === 1 ? "st" : r === 2 ? "nd" : r === 3 ? "rd" : "th";
  return `${MONTHS[now.getMonth()]} ${d}${suffix}, ${now.getFullYear() - 21}`;
}

// Android setDateAndExpiryByKeyPad / setDOBByKeyPad / setExpiryByKeyPad: the digit buffer (≤ 8)
// is shown through the "MMDDYYYY" template until complete; at 8 digits month/day/year are CLAMPED
// (month 1..12, year 1900..currentYear for a DOB / 1900..2100 for an expiry, day ≤ month length).
// Returns "MM/DD/YYYY" (template letters stand in for missing digits).
export function maskDateBuffer(buffer, { expiry = false, now = new Date() } = {}) {
  let clean = String(buffer || "").replace(/\D/g, "").slice(0, 8);
  if (!clean) return "";
  if (clean.length < 8) {
    clean += "MMDDYYYY".slice(clean.length);
  } else {
    let month = parseInt(clean.slice(0, 2), 10);
    let day = parseInt(clean.slice(2, 4), 10);
    let year = parseInt(clean.slice(4, 8), 10);
    const currentYear = now.getFullYear();
    month = month < 1 ? 1 : month > 12 ? 12 : month;
    if (expiry) year = year < 1900 ? 1900 : year >= 2100 ? 2100 : year;
    else year = year < 1900 ? 1900 : year >= currentYear ? currentYear : year;
    const max = daysInMonth(year, month);
    if (day > max) day = max;
    clean = `${pad2(month)}${pad2(day)}${String(year).padStart(4, "0")}`;
  }
  return `${clean.slice(0, 2)}/${clean.slice(2, 4)}/${clean.slice(4, 8)}`;
}

export const isCompleteDate = (masked) => /^\d{2}\/\d{2}\/\d{4}$/.test(String(masked || ""));

function parseMDY(str, { strict = false } = {}) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(str || "").trim());
  if (!m) return null;
  const mo = Number(m[1]);
  const d = Number(m[2]);
  const y = Number(m[3]);
  if (strict && (mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo))) return null;
  return new Date(y, mo - 1, d); // lenient (Java default) rolls over like JS
}

// UtilsMethod.calculateAgeNew — the manual-prompt age: year difference, minus one when the birthday
// has not happened yet this year. Unparseable → 0.
export function calculateAgeNew(mmddyyyy, now = new Date()) {
  const dob = parseMDY(mmddyyyy);
  if (!dob) return 0;
  let age = now.getFullYear() - dob.getFullYear();
  if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) age--;
  return age;
}

// Java Calendar.add(MONTH, n): the day-of-month is clamped to the target month's length.
function addMonthsJava(date, n) {
  const d = new Date(date.getTime());
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  d.setDate(Math.min(day, daysInMonth(d.getFullYear(), d.getMonth() + 1)));
  return d;
}

// UtilsMethod.getAgeOfPerson — the SCAN-path age. It subtracts the DOB's year, month-index and
// day-of-month from today one field at a time and reads the resulting year. That is NOT the same as
// calculateAgeNew on the exact birthday (it under-counts by one); kept faithful because the POS
// accepts/refuses the scanned ID on this number.
export function getAgeOfPerson(mmddyyyy, now = new Date()) {
  const dob = parseMDY(mmddyyyy);
  if (!dob) return 0;
  let c = new Date(now.getTime());
  c.setFullYear(c.getFullYear() - dob.getFullYear());
  c = addMonthsJava(c, -dob.getMonth());
  c.setDate(c.getDate() - dob.getDate());
  return c.getFullYear();
}

// UtilsMethod.checkCardExpireNew — STRICT parse; an unparseable date is "not expired" (false).
// Date-only comparison: expired ⇔ the card date is strictly before today.
export function isCardExpired(mmddyyyy, now = new Date()) {
  const d = parseMDY(mmddyyyy, { strict: true });
  if (!d) return false;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return d.getTime() < today.getTime();
}

const fmtServer = (y, mo, d, h, mi, s) => `${String(y).padStart(4, "0")}-${pad2(mo)}-${pad2(d)} ${pad2(h)}:${pad2(mi)}:${pad2(s)}`;

// The manual prompts send the date as "yyyy-MM-dd HH:mm:ss" formatted in UTC from a LOCAL-midnight
// parse (Android's outPutSdf has timeZone UTC) — so a US store sends "1990-01-15 05:00:00".
export function toServerDateUtc(mmddyyyy) {
  const d = parseMDY(mmddyyyy);
  if (!d) return "";
  return fmtServer(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds());
}
// The scan path formats the same pattern in LOCAL time (no timeZone set) → "1990-01-15 00:00:00".
export function toServerDateLocal(date) {
  if (!(date instanceof Date) || isNaN(date)) return "";
  return fmtServer(date.getFullYear(), date.getMonth() + 1, date.getDate(), 0, 0, 0);
}
const fmtMDY = (d) => `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;

// ── ID barcode (AAMVA keyboard-wedge text) ─────────────────────────────────────────────────────
// A wedge scanner types the whole PDF417 payload into the focused field with the record separators
// dropped, e.g. "...DCSDOEDACJOHNDADRDBB01151990DBA01152030DAU...". Tags: DBB = DOB, DBA = expiry,
// DCS/DAC/DAD/DCT = last/first/middle/first-alt name.

// UtilsMethod.getNameFromIDVerification → "First Middle Last -"
export function nameFromScan(text) {
  const t = String(text || "");
  const get = (tag) => {
    const m = new RegExp(`${tag}(.*?)(?=D[A-Z]{2,}|$)`).exec(t);
    return m ? m[1].trim() : "";
  };
  const last = get("DCS");
  let first = get("DAC");
  const middle = get("DAD");
  if (!first) first = get("DCT");
  return [first, middle, `${last} -`].filter((s) => s.length > 0).join(" ");
}

const afterLast = (s, tag) => (s.lastIndexOf(tag) < 0 ? s : s.slice(s.lastIndexOf(tag) + tag.length));
const beforeLast = (s, tag) => (s.lastIndexOf(tag) < 0 ? s : s.slice(0, s.lastIndexOf(tag)));

// AgeVerificationViewModel.verifyAgeFromIdNumber — the 8 characters between DBB and the next tag.
export function dobFieldFromScan(text) {
  const t = String(text || "");
  for (const next of ["DBA", "DBC", "DAU"]) {
    if (new RegExp(`DBB.{8}${next}`).test(t)) return beforeLast(afterLast(t, "DBB"), next);
  }
  const m = /DBB(\d{8})/.exec(t);
  return m ? m[1] : "";
}

// AgeVerificationViewModel.isIdNotExpired → { expired, expiry:"MM/DD/YYYY"|"" }. No/unparseable DBA
// ⇒ not expired. Strict yyyyMMdd first, then MMddyyyy. Expired ⇔ card date (midnight) < now.
export function expiryFromScan(text, now = new Date()) {
  const m = /DBA(.*?)(?=D[A-Z]{2}|$)/.exec(String(text || ""));
  const raw = m ? m[1].trim() : "";
  if (!raw) return { expired: false, expiry: "" };
  const strict = (y, mo, d) => (mo >= 1 && mo <= 12 && d >= 1 && d <= daysInMonth(y, mo) ? new Date(y, mo - 1, d) : null);
  let d = null;
  if (/^\d{8}$/.test(raw)) {
    d = strict(Number(raw.slice(0, 4)), Number(raw.slice(4, 6)), Number(raw.slice(6, 8))) || strict(Number(raw.slice(4, 8)), Number(raw.slice(0, 2)), Number(raw.slice(2, 4)));
  }
  if (!d) return { expired: false, expiry: "" };
  return { expired: d.getTime() < now.getTime(), expiry: fmtMDY(d) };
}

// AgeVerificationViewModel.isAgeTwentyOne → { ok, dateOfBirth, age, dobForIDNumber }.
// "yyyyMMdd" when the first 4 digits are a plausible year (1900..currentYear), else "MMddyyyy".
export function dobFromScanField(field, now = new Date()) {
  const out = { ok: false, dateOfBirth: "00/00/0000", age: 0, dobForIDNumber: "" };
  const s = String(field || "");
  const yearFirst = parseInt(s.slice(0, 4), 10);
  const yearLast = parseInt(s.slice(4, 8), 10);
  const cy = now.getFullYear();
  let y;
  let mo;
  let d;
  if (!isNaN(yearFirst) && yearFirst >= 1900 && yearFirst <= cy) {
    y = yearFirst;
    mo = parseInt(s.slice(4, 6), 10);
    d = parseInt(s.slice(6, 8), 10);
  } else if (!isNaN(yearLast) && yearLast >= 1900 && yearLast <= cy) {
    y = yearLast;
    mo = parseInt(s.slice(0, 2), 10);
    d = parseInt(s.slice(2, 4), 10);
  } else return out;
  if (isNaN(mo) || isNaN(d)) return out;
  const date = new Date(y, mo - 1, d); // lenient, like SimpleDateFormat's default
  out.dateOfBirth = fmtMDY(date);
  out.dobForIDNumber = toServerDateLocal(date);
  out.age = getAgeOfPerson(out.dateOfBirth, now);
  out.ok = out.age >= 21;
  return out;
}

// Full scan verdict — AgeVerificationResult: "success" | "failure" (underage) | "expired" | "unknown".
// Android posts NOTHING when the DBB field is 9+ characters (the dialog stays on "Scanning…");
// the web reports "unknown" for that case so the cashier is never stranded.
export function verifyScannedId(text, now = new Date()) {
  const field = dobFieldFromScan(text).trim();
  if (!field || field.length >= 9) return { kind: "unknown" };
  const customerName = nameFromScan(text);
  const { expired, expiry } = expiryFromScan(text, now);
  const dob = dobFromScanField(field, now);
  const base = { dateOfBirth: dob.dateOfBirth, age: dob.age, dobForIDNumber: dob.dobForIDNumber, customerName, idExpiryDate: expiry };
  if (dob.ok && !expired) return { kind: "success", ...base };
  if (expired) return { kind: "expired", ...base, dobForIDNumber: "" };
  return { kind: "failure", ...base, dobForIDNumber: "" };
}

// PasscodeViewModel.setPasscodeRequestLocal(pin, ROLE_MANAGER): an employee row with this PIN whose
// role is "manager". Role compared case-insensitively (the `employee.role` column is free text).
export function managerPinMatches(employees, pin) {
  const p = String(pin || "");
  if (p.length !== 4) return false;
  return (employees || []).some((e) => e && String(e.pin ?? "") === p && String(e.role ?? "").trim().toLowerCase() === "manager");
}
